<script setup lang="ts">
  import { ref } from 'vue';
  import type { AxiosError } from 'axios';
  import { post } from '~/services/donationScreen';
  import html2canvas from 'html2canvas';
  import FileIcon from '~/assets/Icons/FileIcon.vue';
  import CloseIcon from '~/assets/Icons/CloseIcon.vue';
  import Attachments from '~/assets/Icons/Attachments.vue';

  const open = ref(false);
  const subject = ref('');
  const message = ref('');
  const contactNumber = ref('');
  const contactError = ref('');
  const loading = ref(false);
  const success = ref(false);
  const attachments = ref<File[]>([]);

  const validateContact = () => {
    if (!contactNumber.value) {
      contactError.value = '';
      return true;
    }
    const cleaned = contactNumber.value.replace(/[\s\-()]/g, '');
    const isValid = /^\+?\d{7,15}$/.test(cleaned);
    contactError.value = isValid ? '' : 'Please enter a valid phone number';
    return isValid;
  };

  const waitForNextPaint = () =>
    new Promise<void>(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });

  const getBrowserInfo = () => {
    const ua = navigator.userAgent;

    let os = 'Unknown';
    if (/Windows NT/i.test(ua)) os = 'Windows';
    else if (/Mac OS X/i.test(ua)) os = 'macOS';
    else if (/Linux/i.test(ua)) os = 'Linux';
    else if (/Android/i.test(ua)) os = 'Android';
    else if (/iPhone|iPad/i.test(ua)) os = 'iOS';

    let browser = 'Unknown';
    if (/Chrome\/\d+/i.test(ua) && !/Edg/i.test(ua)) browser = 'Chrome';
    else if (/Edg\/\d+/i.test(ua)) browser = 'Edge';
    else if (/Firefox\/\d+/i.test(ua)) browser = 'Firefox';
    else if (/Safari\/\d+/i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';

    let device = 'Desktop';
    if (/Mobi|Android|iPhone|iPad/i.test(ua)) device = 'Mobile';
    if (/Tablet|iPad/i.test(ua)) device = 'Tablet';

    return { os, browser, device };
  };

  const captureScreenshot = async (): Promise<string> => {
    const supportBox = document.querySelector('.support-box') as HTMLElement;
    const supportBtn = document.querySelector('.support-btn') as HTMLElement;
    const globalLoading = document.querySelector('.global-loading') as HTMLElement;

    if (supportBox) supportBox.style.display = 'none';
    if (supportBtn) supportBtn.style.display = 'none';
    if (globalLoading) globalLoading.style.display = 'none';

    await new Promise(resolve => setTimeout(resolve, 300));

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    const fullWidth = Math.max(
      document.documentElement.scrollWidth,
      document.body.scrollWidth,
      document.documentElement.clientWidth
    );
    const fullHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
      document.documentElement.clientHeight
    );

    const canvas = await html2canvas(document.documentElement, {
      scale: 1,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: fullWidth,
      height: fullHeight,
      scrollX: -scrollX,
      scrollY: -scrollY,
      windowWidth: fullWidth,
      windowHeight: fullHeight,
      x: 0,
      y: 0,
      onclone: (clonedDoc: Document) => {
        clonedDoc.documentElement.style.overflow = 'visible';
        clonedDoc.body.style.overflow = 'visible';
        const clonedSupportBox = clonedDoc.querySelector('.support-box') as HTMLElement;
        const clonedSupportBtn = clonedDoc.querySelector('.support-btn') as HTMLElement;
        const clonedGlobalLoading = clonedDoc.querySelector('.global-loading') as HTMLElement;
        if (clonedSupportBox) clonedSupportBox.style.display = 'none';
        if (clonedSupportBtn) clonedSupportBtn.style.display = 'none';
        if (clonedGlobalLoading) clonedGlobalLoading.style.display = 'none';
      }
    });

    if (supportBox) supportBox.style.display = '';
    if (supportBtn) supportBtn.style.display = '';
    if (globalLoading) globalLoading.style.display = '';

    return canvas.toDataURL('image/png', 0.92);
  };

  const handleFileUpload = (event: Event) => {
    const target = event.target as HTMLInputElement;

    if (!target.files) return;

    const files = Array.from(target.files);

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large (max 10MB)`);
        continue;
      }
      attachments.value.push(file);
    }
  };

  const removeFile = (index: number) => {
    attachments.value.splice(index, 1);
  };

  const convertFilesToBase64 = async () => {
    const result: any[] = [];

    for (const file of attachments.value) {
      const base64 = await new Promise<string>(resolve => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
      });

      result.push({
        filename: file.name,
        content: base64
      });
    }

    return result;
  };

  // const captureRealScreenshot = async (): Promise<string> => {
  //   let stream: MediaStream | null = null;

  //   try {
  //     stream = await navigator.mediaDevices.getDisplayMedia({
  //       video: { frameRate: 30 },
  //       audio: false
  //     });

  //     const video = document.createElement('video');
  //     video.srcObject = stream;
  //     video.muted = true;
  //     await video.play();

  //     await new Promise<void>(resolve => {
  //       const check = () => {
  //         if (video.videoWidth > 0) resolve();
  //         else requestAnimationFrame(check);
  //       };
  //       check();
  //     });

  //     const canvas = document.createElement('canvas');
  //     canvas.width = video.videoWidth;
  //     canvas.height = video.videoHeight;

  //     const ctx = canvas.getContext('2d')!;
  //     ctx.drawImage(video, 0, 0);

  //     return canvas.toDataURL('image/png', 0.95);
  //   } finally {
  //     stream?.getTracks().forEach(t => t.stop());
  //   }
  // };

  const openSupport = () => {
    success.value = false;
    open.value = true;
  };

  const submitTicket = async () => {
    if (!subject.value || !message.value) return;
    if (!validateContact()) return;

    try {
      open.value = false;
      await waitForNextPaint();
      const screenshot = await captureScreenshot();
      const files = await convertFilesToBase64();

      loading.value = true;

      const payload = {
        subject: subject.value,
        description: message.value,
        contact_number: contactNumber.value || undefined,
        screenshot,
        attachments: files,
        current_url: window.location.href,
        browser_info: getBrowserInfo()
      };

      const response = await post<any>('/v1/support/create_ticket', payload);

      if (response.data && response.data.status === 'success') {
        openSuccessfulCustomNotification(response.data.message);
        success.value = true;
        subject.value = '';
        message.value = '';
        contactNumber.value = '';
        attachments.value = [];
        open.value = false;
      }
    } catch (err) {
      const e = err as AxiosError<any>;
      openFailedCustomNotification(e.response?.data?.message || 'Failed to create ticket');
    } finally {
      loading.value = false;
    }
  };
</script>

<template>
  <div>
    <!-- ── Logo Wave Loader ── -->
    <transition name="loader-fade">
      <div v-if="loading" class="global-loading">
        <div class="loader-card">
          <!-- Logo box with letter wave -->
          <div class="logo-wave-box-main">
            <div class="logo-wave-box">
              <span class="lw lw-w" style="--i: 0">E</span>
              <span class="lw lw-w" style="--i: 1">R</span>
              <span class="lw lw-w" style="--i: 2">P</span>
              <span class="lw lw-w" style="--i: 3">4</span>
              <span class="lw lw-y" style="--i: 4">I</span>
              <span class="lw lw-y" style="--i: 5">m</span>
              <span class="lw lw-y" style="--i: 6">p</span>
              <span class="lw lw-y" style="--i: 7">a</span>
              <span class="lw lw-y" style="--i: 8">c</span>
              <span class="lw lw-y" style="--i: 9">t</span>
            </div>
          </div>

          <!-- Thin brand progress line -->
          <div class="progress-track">
            <div class="progress-line"></div>
          </div>

          <!-- Label with animated dots -->
          <p class="loader-text">
            Submitting your ticket
            <span class="td td1">.</span><span class="td td2">.</span><span class="td td3">.</span>
          </p>
        </div>
      </div>
    </transition>
    <button class="support-btn" @click="openSupport">
      <svg width="26" height="26" viewBox="0 0 149 150" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M107.173 78.4019C98.8099 81.7838 89.7823 83.8696 80.3402 84.4101C78.7118 84.5033 77.0711 84.5505 75.4193 84.5505C73.7676 84.5505 72.1269 84.5033 70.4985 84.4101C61.0541 83.8695 52.0246 81.7828 43.6596 78.3995C26.105 71.2994 11.4773 58.4892 2.086 42.2753C16.724 17.0031 44.0835 0 75.4193 0C106.755 0 134.115 17.0031 148.753 42.2753C139.36 58.491 124.73 71.3023 107.173 78.4019Z"
          fill="white"
        />
        <path
          d="M43.6584 84.9808C41.636 84.1629 39.6525 83.2692 37.7114 82.3032C31.4029 86.7414 26.0699 92.2691 22.0543 98.5757C28.8799 109.295 39.5113 117.764 52.27 122.458C58.3497 124.695 64.9124 126.075 71.7765 126.432H78.9295C85.792 126.075 97.8538 132.865 80.339 150C93.0996 145.306 121.825 109.296 128.652 98.5757C124.648 92.2871 119.333 86.7729 113.049 82.3411C111.13 83.2935 109.17 84.1753 107.172 84.9832C98.8087 88.3651 89.7811 90.451 80.339 90.9914C78.7106 91.0846 77.0699 91.1319 75.4181 91.1319C73.7664 91.1319 72.1257 91.0846 70.4973 90.9914C61.053 90.4508 52.0234 88.3641 43.6584 84.9808Z"
          fill="white"
        />
        <rect x="40.0577" y="31.4608" width="70.8775" height="25.2809" rx="12.6404" fill="#162550" />
        <ellipse cx="95.0421" cy="43.961" rx="4.64079" ry="4.63483" fill="#04FED1" />
        <ellipse cx="75.353" cy="106.32" rx="4.64079" ry="4.63483" fill="#162550" />
        <ellipse cx="56.7928" cy="43.961" rx="4.64079" ry="4.63483" fill="#04FED1" />
        <ellipse cx="56.7928" cy="106.32" rx="4.64079" ry="4.63483" fill="#162550" />
        <ellipse cx="93.9186" cy="106.32" rx="4.64079" ry="4.63483" fill="#162550" />
      </svg>
    </button>

    <transition name="support-popup">
      <div v-if="open" class="support-box">
        <div class="header">
          <div>
            <strong>Support</strong>
            <p class="subtitle">Tell us what’s not working.</p>
          </div>
          <button class="close" @click="open = false">✕</button>
        </div>

        <div class="body">
          <div class="bot-msg">
            <span class="wave">👋</span> <strong>Need help?</strong><br />
            Share the details below and our team will look into it.<br />
          </div>

          <div class="field-group">
            <label class="field-label">Subject <span class="required">*</span></label>
            <input
              v-model="subject"
              class="input"
              placeholder="Enter a short title (e.g. Expense approval not working)"
            />
          </div>

          <div class="field-group">
            <label class="field-label">Description <span class="required">*</span></label>
            <textarea
              v-model="message"
              rows="3"
              class="textarea"
              placeholder="Provide details of the issue, including steps performed and any error message displayed..."
            />
          </div>

          <div class="field-group">
            <label class="field-label">Contact Number <span class="optional">(optional)</span></label>
            <input
              v-model="contactNumber"
              class="input"
              :class="{ 'input-error': contactError }"
              type="tel"
              placeholder="e.g. +91 98765 43210"
              @blur="validateContact"
              @input="contactError = ''"
            />
            <span v-if="contactError" class="error-text">{{ contactError }}</span>
          </div>

          <div class="info-banner">
            <span class="info-icon">📸</span>
            <span>Screenshot of your current screen will be taken automatically.</span>
          </div>

          <div class="attachment-row">
            <label class="attach-btn">
              <Attachments class="w-4 h-4" />
              <span class="text-[12px] font-semibold">Add attachment</span>
              <input type="file" multiple hidden @change="handleFileUpload" />
            </label>

            <span v-if="attachments.length" class="file-count"> {{ attachments.length }} file(s) </span>
          </div>

          <!-- FILE LIST -->
          <div v-if="attachments.length" class="file-list">
            <div v-for="(file, index) in attachments" :key="index" class="file-item">
              <span class="file-name">
                <FileIcon class="w-4 h-4" />
                {{ file.name }}
              </span>
              <span class="remove" @click="removeFile(index)">
                <CloseIcon class="w-4 h-4" />
              </span>
            </div>
          </div>
        </div>

        <div class="footer">
          <button class="send-btn" :disabled="loading || !subject || !message" @click="submitTicket">
            <template v-if="loading">
              <svg class="spinner" width="20" height="20" viewBox="0 0 50 50">
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  stroke="white"
                  stroke-width="4"
                  stroke-linecap="round"
                /></svg
              >…
            </template>

            <template v-else>
              Submit Ticket
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.86158 3.49678L14.9378 7.03491C18.1122 8.62211 18.1122 11.2178 14.9378 12.805L7.86158 16.3432C3.09998 18.724 1.15731 16.773 3.53811 12.0197L4.25731 10.5896C4.43918 10.2258 4.43918 9.62238 4.25731 9.25865L3.53811 7.82025C1.15731 3.06691 3.10825 1.11598 7.86158 3.49678Z"
                  stroke="white"
                  stroke-width="1.24"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M4.49707 9.92004H8.96107"
                  stroke="white"
                  stroke-width="1.24"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </template>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap');

  .wave {
    font-size: 18px;
    display: inline-block;
    transform-origin: 70% 70%;
    /* makes rotation look like a wrist */
    animation: waveHand 2s ease-in-out infinite;
  }

  @keyframes waveHand {
    0% {
      transform: rotate(0deg);
    }

    10% {
      transform: rotate(14deg);
    }

    20% {
      transform: rotate(-8deg);
    }

    30% {
      transform: rotate(14deg);
    }

    40% {
      transform: rotate(-4deg);
    }

    50% {
      transform: rotate(10deg);
    }

    60% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(0deg);
    }
  }

  .support-popup-enter-from {
    opacity: 0;
    transform: translateX(30px) scale(0.95);
  }

  .support-popup-enter-active {
    transition: all 0.9s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .support-popup-enter-to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }

  .support-popup-leave-from {
    opacity: 1;
    transform: translateX(0) scale(1);
  }

  .support-popup-leave-active {
    transition: all 0.5s ease-in;
  }

  .support-popup-leave-to {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }

  .support-btn {
    position: fixed;
    bottom: 30px;
    right: 24px;
    width: 45px;
    height: 45px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2563eb, #1e40af);
    color: #fff;
    font-size: 28px;
    border: none;
    cursor: pointer;
    /* Bounded well below Ant Design's drawer/modal z-index (1000) via the .support-bot
       wrapper in app.vue (z-index: 40) — kept explicit here for clarity, not because
       this value escapes that parent stacking context. */
    z-index: 10;

    display: flex;
    align-items: center;
    justify-content: center;

    /* subtle glow */
    box-shadow: 0 0 0 rgba(37, 99, 220, 0.6);
    animation: pulseGlow 2.5s infinite;
  }

  .support-btn::before {
    content: '';
    position: absolute;
    inset: -10px;
    border-radius: 50%;
    border: 2px solid rgba(37, 99, 220, 0.6);
    animation: ringPulse 2.5s infinite;
  }

  @keyframes ringPulse {
    0% {
      transform: scale(0.8);
      opacity: 0.8;
    }

    70% {
      transform: scale(1.05);
      opacity: 0;
    }

    100% {
      opacity: 0;
    }
  }

  @keyframes pulseGlow {
    0% {
      box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.5);
    }

    70% {
      box-shadow: 0 0 0 18px rgba(37, 99, 235, 0);
    }

    100% {
      box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
    }
  }

  .support-btn:hover {
    transform: scale(1.05);
  }

  .support-box {
    position: fixed;
    bottom: 25px;
    right: 75px;
    width: 430px;
    max-height: 600px;
    background: #fff;
    border-radius: 14px;
    box-shadow:
      0 20px 50px rgba(0, 0, 0, 0.18),
      0 0 0 1px rgba(0, 0, 0, 0.04);
    /* See .support-btn above — bounded by the .support-bot wrapper's z-index in app.vue. */
    z-index: 10;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .header {
    background: #1e3a8a;
    color: #fff;
    padding: 14px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: 'Inter', sans-serif;
    position: relative;
    border-bottom: 2px solid #f8ff2a;
  }

  .subtitle {
    font-size: 12px;
    margin-top: 2px;
    font-family: 'Inter', sans-serif;
    color: #f8ff2a;
    letter-spacing: 0.2px;
  }

  .close {
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    transition: all 0.2s ease;
  }

  .close:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.35);
    transform: rotate(90deg);
  }

  .body {
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  .bot-msg {
    background: #f1f5f9;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.5;
    border-left: 3px solid #162550;
  }

  .field-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .field-label {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    font-family: 'Inter', sans-serif;
  }

  .required {
    color: #ef4444;
  }

  .optional {
    font-weight: 400;
    color: #9ca3af;
    font-size: 11px;
  }

  .input,
  .textarea {
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 9px 12px;
    font-size: 13px;
    font-family: 'Inter', sans-serif !important;
    transition:
      border-color 0.2s,
      box-shadow 0.2s;
    outline: none;
  }

  .input:focus,
  .textarea:focus {
    border-color: #162550;
    box-shadow: 0 0 0 3px rgba(22, 37, 80, 0.08);
  }

  .input-error {
    border-color: #ef4444 !important;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.08) !important;
  }

  .error-text {
    font-size: 11px;
    color: #ef4444;
    font-family: 'Inter', sans-serif;
  }

  .input::placeholder,
  .textarea::placeholder {
    color: #9ca3af;
  }

  .info-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    font-size: 11px;
    font-weight: 500;
    color: #162550;
    font-family: 'Inter', sans-serif;
    background: linear-gradient(135deg, #c9deff, #c9deff);
    padding: 7px 10px;
    border-radius: 8px;
    border: 1px solid #7399ff;
  }

  .info-icon {
    font-size: 15px;
    flex-shrink: 0;
  }

  .footer {
    padding: 12px 16px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    border-top: 1px solid #f3f4f6;
  }

  .send-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #1e3a8a;
    color: #fff;
    padding: 9px 20px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Inter', sans-serif;
    transition:
      opacity 0.2s,
      transform 0.1s;
  }

  .send-btn:hover:not(:disabled) {
    opacity: 0.92;
  }

  .send-btn:active:not(:disabled) {
    transform: scale(0.98);
  }

  .send-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }

  .toast {
    position: fixed;
    bottom: 100px;
    right: 24px;
    background: #ecfdf5;
    color: #065f46;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 13px;
    z-index: 10;
  }

  /* ── Overlay ── */
  .global-loading {
    position: fixed;
    inset: 0;
    background: rgba(10, 18, 40, 0.5);
    backdrop-filter: blur(6px);
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Card entrance transition */
  .loader-fade-enter-active {
    transition:
      opacity 0.25s ease,
      transform 0.25s ease;
  }
  .loader-fade-leave-active {
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }
  .loader-fade-enter-from {
    opacity: 0;
    transform: scale(0.94);
  }
  .loader-fade-leave-to {
    opacity: 0;
    transform: scale(0.96);
  }

  /* Card */
  .loader-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    background: #ffffff;
    border-radius: 16px;
    padding: 36px 52px 28px;
    box-shadow:
      0 8px 32px rgba(30, 58, 138, 0.18),
      0 2px 8px rgba(0, 0, 0, 0.1);
    animation: cardIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }

  @keyframes cardIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  /* Outer navy wrapper — matches the Navbar link bg */
  .logo-wave-box-main {
    background-color: #1e3a8a;
    padding: 5px;
    border-radius: 4px;
  }

  /* Inner white box with white border — exact Navbar logo */
  .logo-wave-box {
    display: flex;
    align-items: center;
    gap: 0;
    background: #1e3a8a;
    border: 2px solid #ffffff;
    padding: 7px 14px;
    height: 46px;
    overflow: visible;
  }

  /* Individual animated letters */
  .lw {
    display: inline-block;
    font-size: 20px;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    line-height: 1;
    animation: letterWave 1.4s ease-in-out infinite;
    animation-delay: calc(var(--i) * 0.1s);
  }

  .lw-w {
    color: #ffffff;
  }
  .lw-y {
    color: #d7dd30;
    font-weight: 400;
  }

  @keyframes letterWave {
    0%,
    55%,
    100% {
      transform: translateY(0);
      opacity: 1;
    }
    28% {
      transform: translateY(-10px);
      opacity: 0.9;
    }
  }

  /* ── Thin brand progress line ── */
  .progress-track {
    width: 160px;
    height: 3px;
    background: #e9ecef;
    border-radius: 99px;
    overflow: hidden;
  }

  .progress-line {
    height: 100%;
    width: 40%;
    border-radius: 99px;
    background: linear-gradient(90deg, #1e3a8a, #d7dd30, #1e3a8a);
    background-size: 200% 100%;
    animation: progressSweep 1.4s ease-in-out infinite;
  }

  @keyframes progressSweep {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -100% 0;
    }
  }

  /* ── Label with typing dots ── */
  .loader-text {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    font-family: 'Inter', sans-serif;
    letter-spacing: 0.2px;
    margin: 0;
  }

  .td {
    display: inline-block;
    animation: tdBlink 1.2s ease-in-out infinite;
    color: #1e3a8a;
    font-weight: 700;
  }
  .td1 {
    animation-delay: 0s;
  }
  .td2 {
    animation-delay: 0.2s;
  }
  .td3 {
    animation-delay: 0.4s;
    color: #d7dd30;
  }

  @keyframes tdBlink {
    0%,
    80%,
    100% {
      opacity: 0.2;
      transform: translateY(0);
    }
    40% {
      opacity: 1;
      transform: translateY(-3px);
    }
  }

  .attachment-section {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .attach-btn {
    padding: 8px 0px 8px 0px;
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    width: fit-content;
    display: flex;
    flex-direction: row;
    gap: 12px;
  }

  .file-list {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .file-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    color: #374151;
    font-weight: 500;
    background: #f9fafb;
    border: 1px solid #f3f4f6;
  }

  .file-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 260px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
  }

  .remove {
    cursor: pointer;
    color: rgb(0, 0, 0);
    font-weight: bold;
  }

  .attachment-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .file-count {
    font-size: 11px;
    color: #64748b;
  }
</style>
